// Read-only code viewer: CodeMirror 6 + TreeSitter syntax highlighting
// Adapted from paradox playground — stripped to bare read-only viewer
//
// Theme strategy: this file does NOT react to theme changes. All syntax
// coloring is class-based — tree-sitter captures resolve to `cm-keyword`,
// `cm-string`, etc. via `captureToClass` in highlight-core.ts, and those
// classes are colored from `--c-syn-*` CSS custom properties declared in
// root.html.heex. The values of those vars are swapped at the CSS layer by
// `:root[data-theme="<name>"]` blocks emitted into module.css from
// theme.dox; flipping `<html data-theme="…">` recascades the colors with
// no JS work. Inline `style="color:rgb(var(--c-…))"` strings in the
// blame gutter markers below also use `var()`, so they retrack the
// active theme through the same cascade. The Theme/Font enums emitted by
// paradox into .dox/index.ts are deliberately NOT imported here — type-
// safe theme switching is server-side, the browser only sees the data-
// attribute. The startup assertion in app.ts (assertSyntaxColorsWired)
// fails loudly if the CSS layer is ever broken or unloaded.

import { EditorView, basicSetup } from "codemirror";
import {
  ViewPlugin,
  Decoration,
  GutterMarker,
  gutter,
  lineNumberMarkers,
} from "@codemirror/view";
import type { ViewUpdate, DecorationSet } from "@codemirror/view";
import {
  RangeSetBuilder,
  StateField,
  StateEffect,
  Compartment,
} from "@codemirror/state";
import type { Extension, Range, RangeSet, Text } from "@codemirror/state";
import { foldService } from "@codemirror/language";
import { autocompletion } from "@codemirror/autocomplete";
import { setDiagnostics } from "@codemirror/lint";
import { hoverTooltip } from "@codemirror/view";
import { vim } from "@replit/codemirror-vim";
import { illuminateCssClassCodeBrowser } from "../../.dox/ui";
import type { BlameBlock } from "../../.dox/ui";

// CSS overlay class names shared with the HEEx `<style>` block in
// `lib/operation_web/live/repo_show_live.ex`. Pinned through
// `CssClassCodeBrowser`. Unprefixed CodeMirror classes (`cm-editor`,
// `cm-scroller`, `cm-gutters`) are CodeMirror's public API and live as
// raw strings — they're a 3rd-party namespace, not ours to pin.
const CM_BLAME_ANNOTATION = illuminateCssClassCodeBrowser("cmBlameAnnotation");
const CM_COMMENTED_LINE = illuminateCssClassCodeBrowser("cmCommentedLine");
const CM_HIGHLIGHTED_LINE = illuminateCssClassCodeBrowser("cmHighlightedLine");
const CM_BLAME_GUTTER = illuminateCssClassCodeBrowser("cmBlameGutter");

import type { LSPClient, LspDiagnostic } from "./lsp-client";
import {
  makeLspCompletionSource,
  makeLspHover,
  lspDiagnosticsToCm,
} from "./lsp-client";

// Shared highlighting pipeline (also used by diff WebWorkers)
import escapeHtml from "escape-html";
import {
  resolveCapture,
  loadLanguage,
  getLoadedLanguage,
  highlightCodeToLines,
  splitLinesPreserveEmpty,
  findLineIndex,
  buildHighlightedLineHtml,
} from "./highlight-core";
import type { TsLangData } from "./highlight-core";
import { stampCmHighlighted } from "./highlight-core-pure";
import {
  parseLineHash as pureParseLineHash,
  viewportBounds as pureViewportBounds,
  shaToHsl as pureShaToHsl,
  minimalChange,
} from "./codemirror-pure";
import type {
  Tree,
  Query,
  Node as TsNode,
  QueryCapture as TsCapture,
} from "web-tree-sitter";
import { Parser } from "web-tree-sitter";
import * as Storage from "./local-storage";

// ---------------------------------------------------------------------------
// Blame entry type for blame gutter — `BlameBlock` is dox-owned
// (`domain/ui/editor.dox`) and imported above. Shape was previously
// hand-rolled here; the dox declaration is now the single source of
// truth shared with the Elixir producer (`build_blame_blocks/1` in
// `repo_show_live.ex`).
// ---------------------------------------------------------------------------

// Types and TreeSitter init imported from highlight-core.ts

// HTML escaping, capture resolution, line highlighting, language loading,
// and WASM name resolution all imported from highlight-core.ts

// ---------------------------------------------------------------------------
// Decoration building
// ---------------------------------------------------------------------------

const decoCache: Record<string, Decoration> = {};
const getDeco = (c: string): Decoration =>
  decoCache[c] || (decoCache[c] = Decoration.mark({ class: c }));

// Cache parsed tree by CodeMirror Text identity — same reference between
// non-mutating transactions, so the check is O(1) pointer comparison
// instead of O(n) string comparison.
let tsTreeCache: { doc: Text; tree: Tree; lang: string } | null = null;

const parseTsTree = (
  parser: Parser,
  doc: Text,
  langId: string,
): Tree | null => {
  if (tsTreeCache && tsTreeCache.lang === langId && tsTreeCache.doc === doc) {
    return tsTreeCache.tree;
  }
  const tree = parser.parse(doc.toString());
  if (tree) {
    tsTreeCache = { doc, tree, lang: langId };
  }
  return tree;
};

// Cache captures alongside tree — only re-query when tree changes
let tsCaptureCache: { tree: Tree; captures: TsCapture[] } | null = null;

const getCachedCaptures = (tree: Tree, query: Query): TsCapture[] => {
  if (tsCaptureCache && tsCaptureCache.tree === tree) {
    return tsCaptureCache.captures;
  }
  const captures = query.captures(tree.rootNode);
  // Sort once — reused across viewport rebuilds
  captures.sort(
    (a: TsCapture, b: TsCapture) =>
      a.node.startIndex - b.node.startIndex ||
      b.node.endIndex - a.node.endIndex,
  );
  tsCaptureCache = { tree, captures };
  return captures;
};

// Viewport bounds (50% margin for smooth scrolling) moved to
// codemirror-pure.ts so it can be property-tested without dragging in
// CodeMirror/tree-sitter; re-bound here.
const viewportBounds = pureViewportBounds;

const buildTsDecorations = (
  view: EditorView,
  tsData: TsLangData | null,
  langId: string,
): DecorationSet => {
  if (!tsData?.query) return Decoration.none;
  const { parser, query } = tsData;
  const tree = parseTsTree(parser, view.state.doc, langId);
  if (!tree) return Decoration.none;
  const captures = getCachedCaptures(tree, query);
  if (captures.length === 0) return Decoration.none;

  const { from: vFrom, to: vTo } = viewportBounds(
    view.visibleRanges,
    view.state.doc.length,
  );

  // Binary search to first capture overlapping viewport
  let lo = 0,
    hi = captures.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (captures[mid]!.node.endIndex <= vFrom) lo = mid + 1;
    else hi = mid;
  }

  const builder = new RangeSetBuilder<Decoration>();
  let lastEnd = 0;
  for (let i = lo; i < captures.length; i++) {
    const { name, node } = captures[i]!;
    if (node.startIndex >= vTo) break;
    const cls = resolveCapture(name);
    if (!cls) continue;
    if (node.startIndex >= lastEnd && node.startIndex < node.endIndex) {
      builder.add(node.startIndex, node.endIndex, getDeco(cls));
      lastEnd = node.endIndex;
    }
  }
  return builder.finish();
};

// ---------------------------------------------------------------------------
// ViewPlugin that applies tree-sitter highlighting
// ---------------------------------------------------------------------------

const treeSitterExtension = (langId: string): Extension =>
  ViewPlugin.fromClass(
    class {
      decorations: DecorationSet = Decoration.none;
      _langData: TsLangData | null = null;

      constructor(view: EditorView) {
        void this._load(view);
      }

      async _load(view: EditorView): Promise<void> {
        try {
          this._langData = await loadLanguage(langId);
          this.decorations = buildTsDecorations(view, this._langData, langId);
          view.dispatch();
        } catch {
          // Language not available — render without highlighting
        }
      }

      update(update: ViewUpdate): void {
        if ((update.docChanged || update.viewportChanged) && this._langData) {
          this.decorations = buildTsDecorations(
            update.view,
            this._langData,
            langId,
          );
        }
      }
    },
    { decorations: (v: { decorations: DecorationSet }) => v.decorations },
  );

// ---------------------------------------------------------------------------
// Line highlighting extension (#L42, #L42-L58)
// ---------------------------------------------------------------------------

interface LineRange {
  readonly from: number;
  readonly to: number;
}

const setLineHighlight = StateEffect.define<LineRange | null>();

const lineHighlightField = StateField.define<LineRange | null>({
  create: () => null,
  update(value, tr) {
    for (const e of tr.effects) {
      if (e.is(setLineHighlight)) return e.value;
    }
    return value;
  },
});

const lineHighlightDeco = EditorView.decorations.compute(
  [lineHighlightField],
  (state) => {
    const range = state.field(lineHighlightField);
    if (!range) return Decoration.none;
    const builder = new RangeSetBuilder<Decoration>();
    const doc = state.doc;
    const fromLine = Math.max(1, Math.min(range.from, doc.lines));
    const toLine = Math.max(fromLine, Math.min(range.to, doc.lines));
    for (let i = fromLine; i <= toLine; i++) {
      const line = doc.line(i);
      builder.add(
        line.from,
        line.from,
        Decoration.line({ class: CM_HIGHLIGHTED_LINE }),
      );
    }
    return builder.finish();
  },
);

// parseLineHash moved to codemirror-pure.ts for property-test access.
const parseLineHash = pureParseLineHash;

const lineClickExtension = (): Extension =>
  EditorView.domEventHandlers({
    click(event: MouseEvent, view: EditorView) {
      const target = event.target as HTMLElement;
      const gutterEl = target.closest(".cm-gutterElement");
      if (!gutterEl) return false;
      const lineNumGutter = gutterEl.closest(".cm-lineNumbers");
      if (!lineNumGutter) return false;

      const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
      if (pos === null) return false;
      const line = view.state.doc.lineAt(pos).number;

      if (event.shiftKey) {
        const current = view.state.field(lineHighlightField);
        const from = current ? current.from : line;
        const range: LineRange = {
          from: Math.min(from, line),
          to: Math.max(from, line),
        };
        view.dispatch({ effects: setLineHighlight.of(range) });
        const hashStr =
          range.from === range.to
            ? `#L${range.from}`
            : `#L${range.from}-L${range.to}`;
        history.replaceState(null, "", hashStr);
      } else {
        const range: LineRange = { from: line, to: line };
        view.dispatch({ effects: setLineHighlight.of(range) });
        history.replaceState(null, "", `#L${line}`);
      }
      return true;
    },
  });

const lineHighlightExtension = (): Extension => [
  lineHighlightField,
  lineHighlightDeco,
  lineClickExtension(),
];

// Highlight a range and scroll its first line into view — the URL hash
// on load, and a thread clicked in the comments panel.
const highlightAndReveal = (view: EditorView, range: LineRange): void => {
  const line = view.state.doc.line(
    Math.max(1, Math.min(range.from, view.state.doc.lines)),
  );
  view.dispatch({
    effects: [
      setLineHighlight.of(range),
      EditorView.scrollIntoView(line.from, { y: "start", yMargin: 80 }),
    ],
  });
};

// Apply initial line highlight from URL hash
const applyInitialLineHighlight = (view: EditorView): void => {
  const range = parseLineHash(window.location.hash);
  if (range) highlightAndReveal(view, range);
};

// Highlight one line of the editor in `container` and scroll to it,
// recording it in the URL hash as a line-number click does.
const revealLine = (container: Element, line: number): void => {
  const view = getView(container);
  if (!view) return;
  highlightAndReveal(view, { from: line, to: line });
  history.replaceState(null, "", `#L${line}`);
};

// ---------------------------------------------------------------------------
// TreeSitter-based code folding
// ---------------------------------------------------------------------------

// Node types that represent foldable blocks, by language
const foldableNodeTypes: Record<string, ReadonlySet<string>> = {
  elixir: new Set(["do_block"]),
  javascript: new Set([
    "statement_block",
    "class_body",
    "object",
    "array",
    "switch_body",
  ]),
  typescript: new Set([
    "statement_block",
    "class_body",
    "object",
    "array",
    "switch_body",
    "interface_body",
    "enum_body",
  ]),
  python: new Set(["block"]),
  rust: new Set([
    "block",
    "field_declaration_list",
    "enum_variant_list",
    "match_block",
    "use_list",
  ]),
  haskell: new Set(["where_clause", "do_expression"]),
  go: new Set(["block", "literal_value", "interface_type", "struct_type"]),
  c: new Set(["compound_statement"]),
  cpp: new Set(["compound_statement", "field_declaration_list"]),
  ruby: new Set(["body_statement", "do_block"]),
  java: new Set(["block", "class_body", "interface_body", "enum_body"]),
  nix: new Set(["attrset_expression", "let_expression", "list_expression"]),
};

// Pre-compute all fold ranges in a single tree walk, cached per tree identity.
// Avoids repeated .children WASM calls from per-line foldService queries.
let foldMapCache: {
  tree: Tree;
  map: Map<number, { from: number; to: number }>;
} | null = null;

const buildFoldMap = (
  tree: Tree,
  foldable: ReadonlySet<string>,
  doc: { line(n: number): { from: number; to: number }; lines: number },
): Map<number, { from: number; to: number }> => {
  if (foldMapCache && foldMapCache.tree === tree) return foldMapCache.map;

  const map = new Map<number, { from: number; to: number }>();

  // Single depth-first walk — one .children call per node, total
  const walk = (node: TsNode): void => {
    if (
      foldable.has(node.type) &&
      node.endPosition.row > node.startPosition.row
    ) {
      const lineNum = node.startPosition.row; // 0-indexed
      const cmLine = lineNum + 1; // 1-indexed for CodeMirror
      // Only keep the deepest (last-written) fold per line
      const foldFrom = doc.line(cmLine).to;
      const foldTo = doc.line(
        Math.min(node.endPosition.row + 1, doc.lines),
      ).from;
      if (foldTo > foldFrom) {
        map.set(cmLine, { from: foldFrom, to: foldTo });
      }
    }
    // Recurse — one .children access per node in the entire tree
    for (const child of node.children) {
      if (child) walk(child);
    }
  };

  walk(tree.rootNode);
  foldMapCache = { tree, map };
  return map;
};

const treeSitterFoldExtension = (langId: string): Extension =>
  foldService.of((state, lineStart, _lineEnd) => {
    const data = getLoadedLanguage(langId);
    if (!data) return null;

    const tree = parseTsTree(data.parser, state.doc, langId);
    const foldable = foldableNodeTypes[langId];
    if (!foldable || !tree) return null;

    const foldMap = buildFoldMap(tree, foldable, state.doc);
    const lineNum = state.doc.lineAt(lineStart).number;
    return foldMap.get(lineNum) || null;
  });

// ---------------------------------------------------------------------------
// Blame gutter extension
// ---------------------------------------------------------------------------

const setBlame = StateEffect.define<readonly BlameBlock[]>();
const clearBlame = StateEffect.define<undefined>();

const blameField = StateField.define<readonly BlameBlock[]>({
  create: () => [],
  update(value, tr) {
    for (const e of tr.effects) {
      if (e.is(setBlame)) return e.value;
      if (e.is(clearBlame)) return [];
    }
    return value;
  },
});

// shaToHsl moved to codemirror-pure.ts for property-test access.
const shaToHsl = pureShaToHsl;

// Relative time from epoch seconds using Intl.RelativeTimeFormat
const rtf = new Intl.RelativeTimeFormat("en", {
  numeric: "auto",
  style: "narrow",
});

const relativeTime = (epoch: number): string => {
  const diff = Math.floor(Date.now() / 1000) - epoch;
  if (diff < 60) return "just now";
  const units: readonly (readonly [number, Intl.RelativeTimeFormatUnit])[] = [
    [31536000, "year"],
    [2592000, "month"],
    [604800, "week"],
    [86400, "day"],
    [3600, "hour"],
    [60, "minute"],
  ];
  for (const [secs, unit] of units)
    if (diff >= secs) return rtf.format(-Math.floor(diff / secs), unit);
  return "just now";
};

// Pre-compute and cache colors by SHA to avoid repeated hashing
const shaColorCache: Record<string, string> = {};
const getShaColor = (sha: string): string =>
  shaColorCache[sha] || (shaColorCache[sha] = shaToHsl(sha));

// Pure helper \u2014 builds the escaped inner HTML for the blame annotation.
// Extracted so XSS escaping can be verified without instantiating CodeMirror.
const buildBlameAnnotationHtml = (block: BlameBlock, color: string): string => {
  const sha = block.hash.slice(0, 8);
  const time = relativeTime(block.authorTime);
  const author =
    block.author.length > 14
      ? block.author.slice(0, 13) + "\u2026"
      : block.author;
  const summary =
    block.summary.length > 30
      ? block.summary.slice(0, 29) + "\u2026"
      : block.summary;

  return (
    `<span style="color:${color};font-family:monospace">${escapeHtml(sha)}</span>` +
    `<span style="margin:0 6px;color:rgb(var(--c-text-tertiary))">${escapeHtml(author)}</span>` +
    `<span style="color:rgb(var(--c-text-tertiary) / 0.7)">${escapeHtml(time)}</span>` +
    `<span style="margin-left:8px;color:rgb(var(--c-text-tertiary) / 0.85);font-style:italic">${escapeHtml(summary)}</span>`
  );
};

class BlameFirstLineMarker extends GutterMarker {
  constructor(
    readonly block: BlameBlock,
    readonly color: string,
  ) {
    super();
  }

  override toDOM(): HTMLElement {
    const el = document.createElement("div");
    el.className = CM_BLAME_ANNOTATION;
    el.style.borderLeft = `3px solid ${this.color}`;
    el.style.paddingLeft = "6px";
    el.style.fontSize = "11px";
    el.style.lineHeight = "inherit";
    el.style.whiteSpace = "nowrap";
    el.style.overflow = "hidden";
    el.style.color = "rgb(var(--c-text-secondary))";
    el.style.cursor = "pointer";

    el.innerHTML = buildBlameAnnotationHtml(this.block, this.color);

    el.title = `${this.block.hash}\n${this.block.author} <${this.block.authorMail}>\n${new Date(this.block.authorTime * 1000).toLocaleString()}\n\n${this.block.summary}`;

    return el;
  }

  override eq(other: GutterMarker): boolean {
    return other instanceof BlameFirstLineMarker && other.block === this.block;
  }
}

class BlameContinuationMarker extends GutterMarker {
  constructor(readonly color: string) {
    super();
  }

  override toDOM(): HTMLElement {
    const el = document.createElement("div");
    el.style.borderLeft = `3px solid ${this.color}`;
    el.style.height = "100%";
    el.style.paddingLeft = "6px";
    return el;
  }

  override eq(other: GutterMarker): boolean {
    return (
      other instanceof BlameContinuationMarker && other.color === this.color
    );
  }
}

// Share continuation marker instances per color — identical for same color
const continuationMarkerCache: Record<string, BlameContinuationMarker> = {};
const getContinuationMarker = (color: string): BlameContinuationMarker =>
  continuationMarkerCache[color] ||
  (continuationMarkerCache[color] = new BlameContinuationMarker(color));

// Stable empty marker set — same reference every time so CodeMirror's
// diff sees no change and skips updateGutters entirely when blame is off.
const emptyMarkers = new RangeSetBuilder<GutterMarker>().finish();

// Cache the built markers so updateGutters gets the same reference
// on re-renders when blame data hasn't changed.
let blameMarkerCache: {
  blocks: readonly BlameBlock[];
  markers: RangeSet<GutterMarker>;
} | null = null;

const blameGutterExtension = (): Extension => [
  blameField,
  gutter({
    class: CM_BLAME_GUTTER,
    markers: (view) => {
      const blocks = view.state.field(blameField);
      if (blocks.length === 0) return emptyMarkers;

      // Return cached markers if blame blocks haven't changed
      if (blameMarkerCache && blameMarkerCache.blocks === blocks) {
        return blameMarkerCache.markers;
      }

      const doc = view.state.doc;
      const builder = new RangeSetBuilder<GutterMarker>();
      for (const block of blocks) {
        const color = getShaColor(block.hash);
        const end = Math.min(block.lineEnd, doc.lines);
        for (let line = block.lineStart; line <= end; line++) {
          const pos = doc.line(line).from;
          const marker =
            line === block.lineStart
              ? new BlameFirstLineMarker(block, color)
              : getContinuationMarker(color);
          builder.add(pos, pos, marker);
        }
      }
      const result = builder.finish();
      blameMarkerCache = { blocks, markers: result };
      return result;
    },
  }),
];

// ---------------------------------------------------------------------------
// Line-number gutter `data-testid` stamper
//
// CodeMirror's built-in line-numbers gutter renders one
// `<div class="cm-gutterElement">N</div>` per visible line. Wallaby
// feature tests need a stable selector for "line N" that doesn't
// depend on iterating the gutter and matching `textContent` (the
// `code_comments_test.exs` execute_script trick). This ViewPlugin
// walks the line-number gutter after every measured update and
// stamps `data-testid="code-gutter-line-<N>"` on each element. The
// testid spelling is owned by `OperationWeb.Ids.code_gutter_line/1`;
// a typo here surfaces as a Wallaby `assert_has` failure pointing
// at this comment.
//
// We attach in `update()` (not `constructor`) because the gutter
// DOM is not yet rendered when the plugin is constructed; CodeMirror
// builds it during the first measure pass. Each pass is cheap — only
// touches visible-viewport elements (CodeMirror itself manages the
// recycle pool), and the writes are idempotent (no-op when the
// attribute already matches).
// ---------------------------------------------------------------------------

const stampGutterTestIds = (root: HTMLElement): void => {
  const gutter = root.querySelector(".cm-lineNumbers");
  if (!gutter) return;
  const els = gutter.querySelectorAll(".cm-gutterElement");
  for (const el of els) {
    const text = el.textContent.trim();
    if (!text) continue;
    const n = Number.parseInt(text, 10);
    if (!Number.isFinite(n) || n < 1) continue;
    const desired = `code-gutter-line-${String(n)}`;
    if (el.getAttribute("data-testid") !== desired) {
      el.setAttribute("data-testid", desired);
    }
  }
};

const gutterTestIdExtension = (): Extension =>
  // An updateListener fires AFTER every plugin has run for the update
  // (a ViewPlugin's update runs in extension order, BEFORE the built-in
  // gutters plugin rebuilds its cells — so stamps lagged one update and
  // Wallaby could click a line whose testid had not caught up). The
  // listener form is order-independent and synchronous; the constructor
  // rAF dance is gone because updateOrInitViewer stamps explicitly
  // right after `new EditorView` (the gutter DOM exists by then).
  EditorView.updateListener.of((u) => {
    if (u.docChanged || u.viewportChanged || u.geometryChanged) {
      stampGutterTestIds(u.view.dom);
    }
  });

// ---------------------------------------------------------------------------
// Commented-line markers
// ---------------------------------------------------------------------------
//
// Comment bodies are rendered server-side in the comments panel
// (`OperationWeb.RepoShowLive`), NOT in the editor. The editor's only
// job is to say WHICH lines carry a thread, which it does by adding
// `cm-commented-line` to the line-number gutter element for those
// lines. That keeps every comment surface in one place — the panel —
// instead of splitting it between a gutter and an overlay that fight
// each other for the same horizontal space.

const setCommentLines = StateEffect.define<readonly number[]>();

const commentLinesField = StateField.define<readonly number[]>({
  create: () => [],
  update(value, tr) {
    for (const e of tr.effects) {
      if (e.is(setCommentLines)) return e.value;
    }
    return value;
  },
});

// Stateless marker — one shared instance, since the only thing it
// carries is the class name.
const commentedLineMarker = new (class extends GutterMarker {
  override elementClass = CM_COMMENTED_LINE;
})();

// Reused for every doc with no comments, so the facet recompute that
// follows an unrelated edit doesn't allocate.
const emptyCommentedLineMarkers = new RangeSetBuilder<GutterMarker>().finish();

const commentedLineMarkers = lineNumberMarkers.compute(
  [commentLinesField, "doc"],
  (state) => {
    const lines = state.field(commentLinesField);
    if (lines.length === 0) return emptyCommentedLineMarkers;
    const doc = state.doc;
    const builder = new RangeSetBuilder<GutterMarker>();
    // RangeSetBuilder demands ascending positions; the server sends
    // lines grouped by thread, which is sorted, but a copy-and-sort
    // here makes the invariant local rather than a cross-language
    // assumption.
    for (const line of [...lines].sort((a, b) => a - b)) {
      if (line >= 1 && line <= doc.lines) {
        const pos = doc.line(line).from;
        builder.add(pos, pos, commentedLineMarker);
      }
    }
    return builder.finish();
  },
);

const commentedLinesExtension = (): Extension => [
  commentLinesField,
  commentedLineMarkers,
];

const setCommentLineNumbers = (
  container: Element,
  lines: readonly number[],
): void => {
  const entry = viewerCache.get(container);
  if (entry) {
    entry.view.dispatch({ effects: setCommentLines.of(lines) });
  }
};

// ---------------------------------------------------------------------------
// Viewer extensions + lifecycle
// ---------------------------------------------------------------------------

// Element-attached editor state, kept in a module-level WeakMap rather
// than slapped onto the DOM node as ad-hoc underscore properties. Avoids
// the `as ViewerContainer` cast lie at every read/write site and lets
// the GC reclaim cache entries when the container leaves the DOM.
interface ViewerCache {
  view: EditorView;
  lang: string;
}

const viewerCache = new WeakMap<Element, ViewerCache>();

// Test-only seam — vitest specs construct a mock EditorView shape and
// need the same `getView(container)` lookup the production code path
// uses (no `_codemirror` field on Element since 312c161cd9 retired the
// ViewerContainer subclass cast). NOT for production callers; the only
// other write paths into `viewerCache` are inside this module.
const __setViewerCacheForTest = (container: Element, view: unknown): void => {
  viewerCache.set(container, {
    view: view as EditorView,
    lang: "test",
  });
};

const isVimMode = (): boolean => Storage.getItem("editorVimMode") === "true";

const setVimMode = (enabled: boolean): void => {
  Storage.setItem("editorVimMode", String(enabled));
};

const vimCompartment = new Compartment();
const editableCompartment = new Compartment();
const lspCompartment = new Compartment();

const buildViewerExtensions = (lang: string): Extension[] => [
  basicSetup,
  vimCompartment.of(isVimMode() ? [vim()] : []),
  editableCompartment.of(EditorView.editable.of(false)),
  lspCompartment.of([]),
  treeSitterExtension(lang),
  lineHighlightExtension(),
  treeSitterFoldExtension(lang),
  blameGutterExtension(),
  commentedLinesExtension(),
  gutterTestIdExtension(),
];

const updateOrInitViewer = (
  container: Element,
  content: string,
  lang: string,
): void => {
  const entry = viewerCache.get(container);

  if (!entry) {
    const v = new EditorView({
      doc: content,
      extensions: buildViewerExtensions(lang),
      parent: container,
    });
    // C14c: the gutter cells exist by the time the constructor returns —
    // stamp immediately so a Wallaby click right after mount can't hit a
    // stale/missing testid.
    stampGutterTestIds(v.dom);
    viewerCache.set(container, { view: v, lang });
    requestAnimationFrame(() => {
      applyInitialLineHighlight(v);
    });
    return;
  }

  // Language changed — recreate with new extensions
  if (entry.lang !== lang) {
    entry.view.destroy();
    viewerCache.delete(container);
    const v = new EditorView({
      doc: content,
      extensions: buildViewerExtensions(lang),
      parent: container,
    });
    // C14c: the gutter cells exist by the time the constructor returns —
    // stamp immediately so a Wallaby click right after mount can't hit a
    // stale/missing testid.
    stampGutterTestIds(v.dom);
    viewerCache.set(container, { view: v, lang });
    requestAnimationFrame(() => {
      applyInitialLineHighlight(v);
    });
    return;
  }

  // Same language — just update content
  entry.view.dispatch({
    changes: {
      from: 0,
      to: entry.view.state.doc.length,
      insert: content,
    },
  });
};

const clearViewerCaches = (): void => {
  tsTreeCache = null;
  tsCaptureCache = null;
  foldMapCache = null;
  blameMarkerCache = null;
};

const destroyViewer = (container: Element): void => {
  const entry = viewerCache.get(container);
  if (entry) {
    entry.view.destroy();
    viewerCache.delete(container);
    clearViewerCaches();
  }
};

// ---------------------------------------------------------------------------
// Export to window
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Static element highlighting (for README code blocks)
// ---------------------------------------------------------------------------

const highlightElement = async (
  el: HTMLElement,
  langId: string,
): Promise<void> => {
  try {
    const code = el.textContent || "";
    const lines = await highlightCodeToLines(code, langId);
    el.innerHTML = lines.join("\n");
    // Stamp `data-cm-highlighted` so Wallaby selectors can assert
    // "this code block was highlighted" without coupling to CodeMirror
    // class-soup (`span[class*='cm-']`). See assets/ts/highlight-core-pure.ts.
    stampCmHighlighted(el);
  } catch {
    // silently fail — code block stays unhighlighted, no stamp
  }
};

// ---------------------------------------------------------------------------
// Blame data API — push/clear blame blocks from outside (LiveView hook)
// ---------------------------------------------------------------------------

const setBlameData = (
  container: Element,
  blocks: readonly BlameBlock[],
): void => {
  const entry = viewerCache.get(container);
  if (entry) {
    entry.view.dispatch({ effects: setBlame.of(blocks) });
  }
};

const clearBlameData = (container: Element): void => {
  const entry = viewerCache.get(container);
  if (entry) {
    entry.view.dispatch({ effects: clearBlame.of(undefined) });
  }
};

const getView = (container: Element): EditorView | undefined =>
  viewerCache.get(container)?.view;

const setEditable = (container: Element, editable: boolean): void => {
  const view = getView(container);
  if (view) {
    view.dispatch({
      effects: editableCompartment.reconfigure(
        EditorView.editable.of(editable),
      ),
    });
  }
};

const enableEditing = (container: Element): void => {
  setEditable(container, true);
};
const disableEditing = (container: Element): void => {
  setEditable(container, false);
};

// Replace the document with `text` as the single smallest edit
// (`minimalChange`), so the selection on untouched lines stays put and
// one undo restores what was there. `updateOrInitViewer`'s same-language
// path replaces `0..doc.length`, which maps every cursor to an edge.
const replaceDocumentMinimal = (container: Element, text: string): void => {
  const view = getView(container);
  if (!view) return;
  const change = minimalChange(view.state.doc.toString(), text);
  if (change.from === change.to && change.insert === "") return;
  view.dispatch({ changes: change });
};

const getDocumentContent = (container: Element): string => {
  const view = getView(container);
  return view ? view.state.doc.toString() : "";
};

// ---------------------------------------------------------------------------
// LSP integration — autocompletion, hover, diagnostics
// ---------------------------------------------------------------------------

// Wire LSP completions, hover, and diagnostics into the view.  Returns a
// disposer that unsubscribes the diagnostics listener and clears the
// compartment — caller holds a single handle instead of tracking three.
const enableLsp = (
  container: Element,
  client: LSPClient,
  uri: string,
): (() => void) => {
  const view = getView(container);
  if (!view) return (): void => {};

  view.dispatch({
    effects: lspCompartment.reconfigure([
      autocompletion({ override: [makeLspCompletionSource(client, uri)] }),
      hoverTooltip(makeLspHover(client, uri)),
    ]),
  });

  const unsubscribe = client.onNotification((n) => {
    if (n.method !== "textDocument/publishDiagnostics") return;
    const params = n.params as { uri?: string; diagnostics?: LspDiagnostic[] };
    if (params.uri !== uri || !params.diagnostics) return;
    const v = getView(container);
    if (!v) return;
    const cmDiags = lspDiagnosticsToCm(v, params.diagnostics);
    v.dispatch(setDiagnostics(v.state, cmDiags));
  });

  return (): void => {
    unsubscribe();
    const v = getView(container);
    if (v) v.dispatch({ effects: lspCompartment.reconfigure([]) });
  };
};

const toggleVimMode = (container: Element): boolean => {
  const entry = viewerCache.get(container);
  if (!entry) return isVimMode();

  const nowEnabled = !isVimMode();
  setVimMode(nowEnabled);

  entry.view.dispatch({
    effects: vimCompartment.reconfigure(nowEnabled ? [vim()] : []),
  });

  return nowEnabled;
};

// ---------------------------------------------------------------------------
// Build log viewer — ANSI-colorized, read-only, streaming-friendly
// ---------------------------------------------------------------------------

import { parseAnsi } from "./hooks/ci-pipeline/helpers";

interface AnsiRange {
  readonly from: number;
  readonly to: number;
  readonly cls: string;
}

const addAnsiRanges = StateEffect.define<readonly AnsiRange[]>();
const clearAnsiRanges = StateEffect.define();

// Store ANSI decorations directly as a DecorationSet — O(log n) position
// mapping via RangeSet.map() instead of O(n) manual mapPos per range,
// and no O(n log n) re-sort on every state recomputation.
const ansiDecoField = StateField.define<DecorationSet>({
  create: () => Decoration.none,
  update(value, tr) {
    let cleared = false;
    const newRanges: Range<Decoration>[] = [];

    for (const e of tr.effects) {
      if (e.is(clearAnsiRanges)) cleared = true;
      if (e.is(addAnsiRanges)) {
        for (const r of e.value) {
          if (r.from < r.to && r.cls) {
            newRanges.push(getDeco(r.cls).range(r.from, r.to));
          }
        }
      }
    }

    const base = cleared
      ? Decoration.none
      : tr.docChanged
        ? value.map(tr.changes)
        : value;

    if (newRanges.length === 0) return base;
    return base.update({ add: newRanges, sort: true });
  },
  provide: (f) => EditorView.decorations.from(f),
});

interface ParsedAnsi {
  readonly clean: string;
  readonly ranges: readonly AnsiRange[];
}

const parseAnsiRanges = (text: string, offset: number): ParsedAnsi => {
  const spans = parseAnsi(text);
  const ranges: AnsiRange[] = [];
  const parts: string[] = [];
  let pos = offset;
  for (const span of spans) {
    parts.push(span.text);
    if (span.classes) {
      ranges.push({ from: pos, to: pos + span.text.length, cls: span.classes });
    }
    pos += span.text.length;
  }
  return { clean: parts.join(""), ranges };
};

interface BuildLogContainer extends Element {
  _buildLogCm?: EditorView | undefined;
  _buildLogAutoScroll?: boolean | undefined;
}

const buildLogExtensions = (): Extension[] => [
  EditorView.editable.of(false),
  EditorView.lineWrapping,
  ansiDecoField,
  EditorView.domEventHandlers({
    scroll(_event, view) {
      const s = view.scrollDOM;
      const atBottom = s.scrollHeight - s.scrollTop - s.clientHeight < 20;
      (view.dom.parentElement as BuildLogContainer)._buildLogAutoScroll =
        atBottom;
    },
  }),
  EditorView.theme({
    "&": {
      height: "100%",
      fontSize: "14px",
      fontFamily: "'DejaVu Mono', monospace",
    },
    ".cm-scroller": { overflow: "auto" },
    ".cm-gutters": { display: "none" },
    ".cm-content": { padding: "8px" },
  }),
];

const initBuildLogViewer = (container: Element): void => {
  const c = container as BuildLogContainer;
  if (c._buildLogCm) return;
  c._buildLogAutoScroll = true;
  c._buildLogCm = new EditorView({
    doc: "",
    extensions: buildLogExtensions(),
    parent: container,
  });
};

const appendBuildLogText = (container: Element, text: string): void => {
  const c = container as BuildLogContainer;
  const view = c._buildLogCm;
  if (!view) return;
  const from = view.state.doc.length;
  const { clean, ranges } = parseAnsiRanges(text, from);
  view.dispatch({
    changes: { from, insert: clean },
    effects: ranges.length > 0 ? [addAnsiRanges.of(ranges)] : [],
  });
};

const setBuildLogContent = (container: Element, content: string): void => {
  const c = container as BuildLogContainer;
  const view = c._buildLogCm;
  if (!view) return;
  const { clean, ranges } = parseAnsiRanges(content, 0);
  view.dispatch({
    changes: { from: 0, to: view.state.doc.length, insert: clean },
    effects: [
      clearAnsiRanges.of(null),
      ...(ranges.length > 0 ? [addAnsiRanges.of(ranges)] : []),
    ],
  });
};

const scrollBuildLogToEnd = (container: Element): void => {
  const c = container as BuildLogContainer;
  const view = c._buildLogCm;
  if (!view) return;
  view.dispatch({
    effects: EditorView.scrollIntoView(view.state.doc.length, { y: "end" }),
  });
};

const destroyBuildLogViewer = (container: Element): void => {
  const c = container as BuildLogContainer;
  if (c._buildLogCm) {
    c._buildLogCm.destroy();
    c._buildLogCm = undefined;
  }
};

const isBuildLogAutoScroll = (container: Element): boolean =>
  (container as BuildLogContainer)._buildLogAutoScroll !== false;

// Re-export highlight-core symbols for backward compat (tests, history-viewer)
export { captureToClass, resolveCapture } from "./highlight-core";

export {
  updateOrInitViewer,
  destroyViewer,
  highlightElement,
  highlightCodeToLines as highlightCodeLines,
  setBlameData,
  clearBlameData,
  setCommentLineNumbers,
  getView,
  revealLine,
  enableEditing,
  disableEditing,
  getDocumentContent,
  replaceDocumentMinimal,
  enableLsp,
  toggleVimMode,
  isVimMode,
  initBuildLogViewer,
  appendBuildLogText,
  setBuildLogContent,
  scrollBuildLogToEnd,
  destroyBuildLogViewer,
  isBuildLogAutoScroll,
};

// `BlameBlock` is now dox-owned (`domain/ui/editor.dox`); downstream
// consumers should import it directly from `.dox/ui` rather than
// re-routing through this file. Kept the re-export to preserve any
// existing import sites, but the source of truth is dox.
export type { BlameBlock };

// Pure helpers — exported for testing
export {
  splitLinesPreserveEmpty,
  findLineIndex,
  shaToHsl,
  relativeTime,
  parseLineHash,
  buildBlameAnnotationHtml,
  buildHighlightedLineHtml,
  buildFoldMap,
  parseAnsiRanges,
  viewportBounds,
  clearViewerCaches,
  __setViewerCacheForTest,
};
